import { useEffect, useState, type ReactNode } from 'react'
import {
  disabledFilterKeys, filterIsRequired, filterVisible, rangeEndsRequired, rangeKeys, type ReportFilterDef,
} from '@project/contracts'
import { Button, Icon, Lookup, Modal, type LookupOption } from '../design/index.js'
import { DATE_PRESETS } from './filters.js'
import { formatDate } from './format.js'

/**
 * A report's filter controls, drawn from its definition (docs/25).
 *
 * On top of the single-value controls: several values at once (RPT-026),
 * the either/or and mutually-disabling rules (RPT-076), an item browse dialog
 * (RPT-116), and the one-line summary of what is applied (RPT-022).
 */

type Values = Record<string, string>

const split = (v: string | undefined): string[] => (v ? v.split(',').filter((x) => x !== '') : [])

/* ----------------------------------------------------------- lookup labels */

const normalise = (raw: unknown): LookupOption[] => {
  const list = Array.isArray(raw) ? raw
    : raw && typeof raw === 'object' ? ((raw as Record<string, unknown>)['items'] ?? (raw as Record<string, unknown>)['rows']) : undefined
  if (!Array.isArray(list)) return []
  return list.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const row = entry as Record<string, unknown>
    const value = String(row['value'] ?? row['id'] ?? '')
    if (!value) return []
    const sub = row['secondary'] ?? row['sublabel']
    return [{
      value,
      label: String(row['label'] ?? row['name'] ?? value),
      ...(typeof sub === 'string' ? { sublabel: sub } : {}),
      ...(row['extra'] && typeof row['extra'] === 'object' ? { extra: row['extra'] as Record<string, string | null> } : {}),
    }]
  })
}

const labelCache = new Map<string, string>()

/** The display name of one looked-up id, the way the Lookup itself resolves it. */
export const useLookupLabel = (entity: string, id: string): string => {
  const cacheKey = `${entity}:${id}`
  const [label, setLabel] = useState(labelCache.get(cacheKey) ?? '')
  useEffect(() => {
    if (!id || labelCache.has(cacheKey)) { setLabel(labelCache.get(cacheKey) ?? ''); return undefined }
    const ctrl = new AbortController()
    fetch(`/api/lookups/${encodeURIComponent(entity)}?id=${encodeURIComponent(id)}`, {
      signal: ctrl.signal, headers: { accept: 'application/json' },
    })
      .then(async (res) => (res.ok ? normalise(await res.json()) : []))
      .then((rows) => {
        const row = rows.find((r) => r.value === id)
        if (row) { labelCache.set(cacheKey, row.label); setLabel(row.label) }
      })
      .catch(() => undefined)
    return () => ctrl.abort()
  }, [entity, id, cacheKey])
  return label
}

export const remember = (entity: string, option: LookupOption | null): void => {
  if (option) labelCache.set(`${entity}:${option.value}`, option.label)
}

export const LookupLabel = ({ entity, id }: { entity: string; id: string }) => {
  const label = useLookupLabel(entity, id)
  return <>{label || '…'}</>
}

/* ----------------------------------------------------- several at once */

const Chip = ({ children, onRemove, name }: { children: ReactNode; onRemove: () => void; name: string }) => (
  <span className="rt-chip">
    {children}
    <button type="button" aria-label={`Remove ${name}`} onClick={onRemove}><Icon name="x" size={14} /></button>
  </span>
)

export const LookupChip = ({ entity, id, onRemove }: { entity: string; id: string; onRemove?: (() => void) | undefined }) => {
  const label = useLookupLabel(entity, id)
  if (!onRemove) return <span className="rt-chip">{label || '…'}</span>
  return <Chip name={label || 'this'} onRemove={onRemove}>{label || '…'}</Chip>
}

/* ----------------------------------------------------------- item browse */

/** A searchable item picker beside an Item filter (RPT-116). */
export const ItemBrowse = ({ entity, onPick, onClose }: {
  entity: string; onPick: (option: LookupOption) => void; onClose: () => void
}) => {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<LookupOption[]>([])
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle')
  useEffect(() => {
    const term = q.trim()
    if (!term) { setRows([]); setState('idle'); return undefined }
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setState('busy')
      fetch(`/api/lookups/${encodeURIComponent(entity)}?q=${encodeURIComponent(term)}`, {
        signal: ctrl.signal, headers: { accept: 'application/json' },
      })
        .then(async (res) => { if (!res.ok) throw new Error(String(res.status)); return normalise(await res.json()) })
        .then((found) => { setRows(found); setState('done') })
        .catch((err: unknown) => { if (!(err instanceof DOMException && err.name === 'AbortError')) setState('failed') })
    }, 250)
    return () => { clearTimeout(timer); ctrl.abort() }
  }, [entity, q])

  return (
    <Modal title="Find an item" wide onClose={onClose}
      actions={<Button kind="ghost" onClick={onClose}>Cancel</Button>}
      footerLeft={state === 'done' ? `${rows.length} found` : ''}>
      <input className="control" autoFocus placeholder="Search by name, code or HSN/SAC" value={q}
        aria-label="Search items" onChange={(e) => setQ(e.currentTarget.value)} />
      <div className="tbl-wrap" style={{ marginTop: 16 }}>
        <table className="tbl">
          <thead><tr>
            <th>Item</th><th style={{ width: 150 }}>Code</th><th style={{ width: 160 }}>Category</th>
            <th style={{ width: 140 }}>Brand</th><th style={{ width: 90 }}>Unit</th><th style={{ width: 130 }}>HSN/SAC</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td className="tbl__none" colSpan={6}>
                {state === 'busy' ? 'Loading…' : state === 'failed' ? 'The search could not be completed. Try again.'
                  : state === 'done' ? 'No items match that.' : 'Please start typing to search.'}
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.value} className="tbl__row--click" tabIndex={0}
                onClick={() => onPick(r)}
                onKeyDown={(e) => { if (e.key === 'Enter') onPick(r) }}>
                <td>{r.label}</td><td className="mono">{r.sublabel ?? ''}</td>
                <td>{r.extra?.['category'] ?? ''}</td><td>{r.extra?.['brand'] ?? ''}</td>
                <td>{r.extra?.['unit'] ?? ''}</td><td>{r.extra?.['hsnSacCode'] ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------- controls */

const RequiredMark = ({ on }: { on: boolean }) =>
  on ? <span className="field__req"><span className="rt-req" aria-hidden="true">*</span> Required</span> : null

/** One filter control, from the report's own definition. */
export const FilterControl = ({ filter, filters, value, onChange }: {
  filter: ReportFilterDef
  /** All of the report's filters, for the group and disabling rules. */
  filters: readonly ReportFilterDef[]
  value: Values
  onChange: (patch: Values) => void
}) => {
  const id = `rf-${filter.key}`
  const required = filterIsRequired(filter, filters, value)
  const disabled = disabledFilterKeys(filters, value).has(filter.key)
  const [browsing, setBrowsing] = useState(false)
  // Not asked in this mode at all (Warehouse under Show by Projects).
  if (!filterVisible(filter, value)) return null
  const group = filter.requiredGroup
    ? filters.filter((f) => f.requiredGroup === filter.requiredGroup && f.key !== filter.key).map((f) => f.label.toLowerCase())
    : []
  const hint = required && group.length > 0 ? `Or set ${group.join(' or ')} instead.` : filter.hint
  const label = (
    <label className="field__label" htmlFor={filter.control === 'dateRange' ? `${id}-from` : id}>
      {filter.label}<RequiredMark on={required} />
    </label>
  )
  const foot = hint ? <div className="field__hint">{hint}</div> : null

  if (filter.control === 'dateRange' && filter.split) {
    // Two dates, each labelled and marked on its own: From date * with To
    // date optional (REP2-004). No presets — the reference has none here.
    const keys = rangeKeys(filter)
    const ends = rangeEndsRequired(filter, filters, value)
    const end = (which: 'from' | 'to') => (
      <div className="field" key={which}>
        <label className="field__label" htmlFor={`${id}-${which}`}>
          {which === 'from' ? filter.split!.fromLabel : filter.split!.toLabel}<RequiredMark on={ends[which]} />
        </label>
        <input id={`${id}-${which}`} className="control" type="date" value={value[keys[which]] ?? ''} disabled={disabled}
          onChange={(e) => onChange({ [keys[which]]: e.currentTarget.value })} />
        {which === 'from' ? foot : null}
      </div>
    )
    return <>{end('from')}{end('to')}</>
  }

  if (filter.control === 'dateRange') {
    // Its own keys when it has them: Approved date beside Document date (RPT-016).
    const keys = rangeKeys(filter)
    return (
      <div className="field">
        {label}
        <div style={{ display: 'flex', gap: 10 }}>
          <input id={`${id}-from`} className="control" type="date" value={value[keys.from] ?? ''} disabled={disabled}
            aria-label={`${filter.label} from`} onChange={(e) => onChange({ [keys.from]: e.currentTarget.value })} />
          <input className="control" type="date" value={value[keys.to] ?? ''} disabled={disabled}
            aria-label={`${filter.label} to`} onChange={(e) => onChange({ [keys.to]: e.currentTarget.value })} />
        </div>
        <div className="presets">
          {DATE_PRESETS.map((preset) => (
            <button key={preset.key} type="button" className="preset" disabled={disabled}
              onClick={() => { const r = preset.range(); onChange({ [keys.from]: r.from, [keys.to]: r.to }) }}>
              {preset.label}
            </button>
          ))}
        </div>
        {foot}
      </div>
    )
  }

  if (filter.control === 'lookup' && filter.lookup) {
    const entity = filter.lookup.entity
    const browse = filter.browse ?? entity === 'item'
    const chosen = split(value[filter.key])
    const pick = (option: LookupOption | null, v: string): void => {
      remember(entity, option)
      if (!filter.multiple) { onChange({ [filter.key]: v }); return }
      if (!v || chosen.includes(v)) return
      onChange({ [filter.key]: [...chosen, v].join(',') })
    }
    return (
      <div className="field">
        {label}
        {filter.multiple && chosen.length > 0 ? (
          <div className="rt-chips">
            {chosen.map((c) => (
              <LookupChip key={c} entity={entity} id={c}
                onRemove={() => onChange({ [filter.key]: chosen.filter((x) => x !== c).join(',') })} />
            ))}
          </div>
        ) : null}
        <div className="rt-lookup-row">
          <Lookup
            // Remounted after each pick, so the box empties for the next one.
            key={filter.multiple ? chosen.length : 'one'}
            entity={entity}
            value={filter.multiple ? '' : value[filter.key] ?? ''}
            onChange={(v, option) => pick(option, v)}
            placeholder={filter.multiple && chosen.length > 0 ? 'Add another' : 'Please start typing to search'}
            disabled={disabled}
          />
          {browse ? (
            <Button kind="ghost" icon={<Icon name="search" size={18} />} disabled={disabled}
              onClick={() => setBrowsing(true)} aria-label={`Browse ${filter.label.toLowerCase()}`}>
              Browse
            </Button>
          ) : null}
        </div>
        {foot}
        {browsing ? (
          <ItemBrowse entity={entity} onClose={() => setBrowsing(false)}
            onPick={(option) => { pick(option, option.value); setBrowsing(false) }} />
        ) : null}
      </div>
    )
  }

  if (filter.control === 'select') {
    const options = filter.options ?? []
    if (filter.multiple) {
      const chosen = split(value[filter.key])
      const nameOf = (v: string): string => options.find((o) => o.value === v)?.label ?? v
      return (
        <div className="field">
          {label}
          {chosen.length > 0 ? (
            <div className="rt-chips">
              {chosen.map((c) => (
                <Chip key={c} name={nameOf(c)}
                  onRemove={() => onChange({ [filter.key]: chosen.filter((x) => x !== c).join(',') })}>
                  {nameOf(c)}
                </Chip>
              ))}
            </div>
          ) : null}
          <select id={id} className="control" value="" disabled={disabled}
            onChange={(e) => {
              const v = e.currentTarget.value
              if (v && !chosen.includes(v)) onChange({ [filter.key]: [...chosen, v].join(',') })
            }}>
            <option value="">{chosen.length ? 'Add another' : 'Any'}</option>
            {options.filter((o) => !chosen.includes(o.value)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {foot}
        </div>
      )
    }
    return (
      <div className="field">
        {label}
        <select id={id} className="control" value={value[filter.key] ?? ''} disabled={disabled}
          onChange={(e) => onChange({ [filter.key]: e.currentTarget.value })}>
          <option value="">Not chosen</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {foot}
      </div>
    )
  }

  if (filter.control === 'date') {
    return (
      <div className="field">
        {label}
        <input id={id} className="control" type="date" value={value[filter.key] ?? ''} disabled={disabled}
          onChange={(e) => onChange({ [filter.key]: e.currentTarget.value })} />
        {foot}
      </div>
    )
  }

  if (filter.control === 'checkbox') {
    return (
      <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 44 }}>
        <input type="checkbox" checked={value[filter.key] === 'true'} disabled={disabled}
          onChange={(e) => onChange({ [filter.key]: e.currentTarget.checked ? 'true' : '' })} />
        {filter.label}
      </label>
    )
  }

  return (
    <div className="field">
      {label}
      <input id={id} className="control" value={value[filter.key] ?? ''} disabled={disabled}
        onChange={(e) => onChange({ [filter.key]: e.currentTarget.value })} />
      {foot}
    </div>
  )
}

/**
 * The draft after a change, with anything the change switched off cleared —
 * so a disabled filter never travels to the server with a stale value.
 */
export const patchFilters = (filters: readonly ReportFilterDef[], draft: Values, patch: Values): Values => {
  const next: Values = { ...draft, ...patch }
  for (const key of disabledFilterKeys(filters, next)) {
    const f = filters.find((x) => x.key === key)
    if (f?.control === 'dateRange') { const k = rangeKeys(f); delete next[k.from]; delete next[k.to] } else delete next[key]
  }
  return next
}

/* ------------------------------------------------------- applied summary */

/** "Project: PRO/221 - X · Period: 01-Apr-2026 – 06-Oct-2026", for a closed drawer (RPT-022). */
export const AppliedFilters = ({ filters, applied }: { filters: readonly ReportFilterDef[]; applied: Values }) => {
  const off = disabledFilterKeys(filters, applied)
  const items = filters.flatMap((f) => {
    if (off.has(f.key)) return []
    if (f.control === 'dateRange' && f.split) {
      const k = rangeKeys(f)
      return [
        ...(applied[k.from] ? [{ f, key: k.from, label: f.split.fromLabel, text: <>{formatDate(applied[k.from]!)}</> }] : []),
        ...(applied[k.to] ? [{ f, key: k.to, label: f.split.toLabel, text: <>{formatDate(applied[k.to]!)}</> }] : []),
      ]
    }
    if (f.control === 'dateRange') {
      const k = rangeKeys(f)
      if (!applied[k.from] && !applied[k.to]) return []
      return [{ f, key: f.key, label: f.label, text: <>{applied[k.from] ? formatDate(applied[k.from]!) : '…'} – {applied[k.to] ? formatDate(applied[k.to]!) : '…'}</> }]
    }
    const v = applied[f.key]
    if (!v) return []
    const item = (text: ReactNode) => [{ f, key: f.key, label: f.label, text }]
    if (f.control === 'checkbox') return v === 'true' ? item(<>Yes</>) : []
    if (f.control === 'date') return item(<>{formatDate(v)}</>)
    const values = split(v)
    if (f.control === 'lookup' && f.lookup) {
      const entity = f.lookup.entity
      return item(<>{values.map((id, i) => <span key={id}>{i ? ', ' : ''}<LookupLabel entity={entity} id={id} /></span>)}</>)
    }
    if (f.control === 'select') {
      return item(<>{values.map((x) => f.options?.find((o) => o.value === x)?.label ?? x).join(', ')}</>)
    }
    return item(<>{v}</>)
  })
  if (items.length === 0) return <div className="rt-applied">No filters applied.</div>
  return (
    <div className="rt-applied" aria-label="Applied filters">
      {items.map(({ key, label, text }) => (
        <span key={key} className="rt-applied__item"><span className="rt-applied__label">{label}:</span> {text}</span>
      ))}
    </div>
  )
}
